import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';

const DreamAnalysisTool = ({ client }) => {
    const [dreams, setDreams] = useState([]);
    const [selectedDream, setSelectedDream] = useState(null);
    const [analysisStep, setAnalysisStep] = useState(0);
    const [analysisData, setAnalysisData] = useState({});
    const [existingAnalysis, setExistingAnalysis] = useState(null); // New state for saved analysis
    const [analyzedDreamIds, setAnalyzedDreamIds] = useState(new Set()); // To track which dreams have analysis

    useEffect(() => {
        loadDreamsAndAnalyses();
    }, [client.id]);

    const loadDreamsAndAnalyses = async () => {
        try {
            const [works, creativeWorks] = await Promise.all([
                DataManager.getClientWorks(client.id, true),
                DataManager.getCreativeWorks(client.id)
            ]);

            // Filter dreams
            const dreamEntries = works.filter(w => {
                if (w.type === 'dream') return w.shared; // Explicitly include type='dream'

                const content = typeof w.content === 'object' ? w.content : { text: w.content };
                const isDreamCat = content.category === 'Sogni';
                const hasDreamTag = (content.userTags || []).some(t => t.toLowerCase().includes('sogno'));
                return (isDreamCat || hasDreamTag) && w.shared;
            }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

            setDreams(dreamEntries);

            // Index analyzed dreams
            const analyzedIds = new Set();
            creativeWorks.forEach(cw => {
                if (cw.type === 'dream_analysis' && cw.content.originalDreamId) {
                    analyzedIds.add(cw.content.originalDreamId);
                }
            });
            setAnalyzedDreamIds(analyzedIds);

        } catch (error) {
            console.error("Error loading data:", error);
        }
    };

    const handleSelectDream = async (dream) => {
        setSelectedDream(dream);
        setAnalysisStep(0);
        setAnalysisData({});
        setExistingAnalysis(null);

        // Check for existing analysis
        try {
            const creativeWorks = await DataManager.getCreativeWorks(client.id);
            const found = creativeWorks.find(cw =>
                cw.type === 'dream_analysis' && cw.content.originalDreamId === dream.id
            );

            if (found) {
                setExistingAnalysis(found);
                setAnalysisData(found.content.analysis);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const saveAnalysis = async () => {
        if (!selectedDream) return;

        // Check availability of data (support both structures during migration)
        const hasLegacyData = steps.some(s => analysisData[s.field]);
        const hasNewData = analysisData.fullAnalysis && analysisData.fullAnalysis.trim();

        if (!hasLegacyData && !hasNewData) {
            alert("Inserisci l'analisi prima di salvare.");
            return;
        }

        try {
            await DataManager.saveCreativeWork({
                clientId: client.id,
                type: 'dream_analysis',
                content: {
                    originalDreamId: selectedDream.id,
                    dreamTitle: selectedDream.content.title,
                    analysis: analysisData,
                    method: 'gestalt_holistic' // Marker for new method
                },
                shared: true
            });

            alert("Analisi salvata correttamente!");
            await loadDreamsAndAnalyses(); // Refresh stats
            handleSelectDream(selectedDream); // Reload view
        } catch (error) {
            console.error(error);
            alert("Errore nel salvataggio dell'analisi.");
        }
    };

    // Gestalt Dream Work Steps (Legacy & Reference)
    const steps = [
        {
            title: "1. Identificazione con gli Elementi",
            description: "Scegli gli elementi chiave del sogno (oggetti, persone). Invita il cliente a 'diventare' quell'elemento e a parlarne in prima persona al presente ('Io sono il gufo e mi sento...').",
            field: "identifications",
            placeholder: "Gufo: 'Sono saggio ma spaventoso, guardo tutti dall'alto...'\nForesta: 'Sono fitta e nascondo segreti...'"
        },
        {
            title: "2. Dialogo tra Parti",
            description: "Se emergono elementi in conflitto o polarità opposte, falli dialogare tra loro (es. Il Gufo parla con la Foresta). Cosa si dicono?",
            field: "dialogue",
            placeholder: "Trascrivi qui i passaggi chiave del dialogo tra le parti scisse..."
        },
        {
            title: "3. Messaggio Esistenziale",
            description: "Qual è il messaggio che questo sogno sta portando alla vita del sognatore oggi? Quale parte di sé sta chiedendo di essere integrata?",
            field: "existentialMessage",
            placeholder: "Il sogno mi dice che devo accettare la mia parte 'oscura' e saggia invece di temerla."
        }
    ];

    const [isCreating, setIsCreating] = useState(false);
    const [newDreamForm, setNewDreamForm] = useState({ title: '', text: '', date: new Date().toISOString().split('T')[0] });

    const handleCreateDream = async () => {
        if (!newDreamForm.text) {
            alert("Inserisci il testo del sogno.");
            return;
        }

        try {
            const payload = {
                clientId: client.id,
                type: 'dream',
                content: {
                    title: newDreamForm.title || "Sogno raccontato in incontro",
                    text: newDreamForm.text,
                    category: 'Sogni',
                    userTags: ['sogno', 'incontro']
                },
                shared: true, // Visible since it's worked on
                timestamp: new Date(newDreamForm.date).toISOString()
            };

            await DataManager.saveWork(payload);
            setIsCreating(false);
            setNewDreamForm({ title: '', text: '', date: new Date().toISOString().split('T')[0] });
            await loadDreamsAndAnalyses();
        } catch (e) {
            console.error("Error creating dream:", e);
            alert("Errore durante il salvataggio.");
        }
    };

    if (selectedDream) {
        return (
            <div className="bg-white rounded-3xl shadow-lg p-6 animate-fade-in">
                <button onClick={handleBack} className="mb-4 text-indigo-600 font-bold hover:underline">
                    ← Torna alla lista sogni
                </button>

                <div className="grid md:grid-cols-2 gap-6">
                    {/* Left: Original Dream */}
                    <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                        <span className="text-xs uppercase font-bold text-gray-400">Testo del Sogno Originale</span>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                            {selectedDream.content.title || "Senza Titolo"}
                        </h3>
                        {/* ... dream content display ... */}
                        <div className="whitespace-pre-wrap text-gray-700 italic border-l-4 border-brand-300 pl-4 mt-4">
                            {selectedDream.content.text || selectedDream.content}
                        </div>
                    </div>

                    {/* Right: Analysis (View or Edit) */}
                    <div className="space-y-6">
                        {existingAnalysis ? (
                            <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="text-2xl">✅</span>
                                    <h3 className="text-lg font-bold text-green-900">Analisi Completata</h3>
                                    <span className="text-xs text-green-700 ml-auto">
                                        {new Date(existingAnalysis.timestamp).toLocaleDateString()}
                                    </span>
                                </div>
                                <div className="space-y-4">
                                    {analysisData.fullAnalysis ? (
                                        <div className="bg-white p-4 rounded-lg shadow-sm">
                                            <h5 className="font-bold text-xs text-indigo-600 uppercase mb-2">Analisi Gestaltica</h5>
                                            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                                                {analysisData.fullAnalysis}
                                            </p>
                                        </div>
                                    ) : (
                                        steps.map((step, i) => (
                                            <div key={i} className="bg-white p-3 rounded-lg shadow-sm">
                                                <h5 className="font-bold text-xs text-indigo-600 uppercase mb-1">{step.title}</h5>
                                                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                                    {analysisData[step.field] || <span className="text-gray-400 italic">Nessuna risposta</span>}
                                                </p>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Single View Analysis Editor */}
                                <div className="bg-indigo-50 p-6 rounded-xl border border-indigo-100">
                                    <h4 className="font-bold text-indigo-900 mb-2">Analisi & Integrazione Gestaltica</h4>
                                    <p className="text-sm text-indigo-800 mb-4">
                                        Trascrivi qui il lavoro di elaborazione svolto con il cliente (identificazioni, dialoghi, consapevolezze emerse).
                                    </p>

                                    <textarea
                                        className="w-full p-4 rounded-xl border border-indigo-200 focus:ring-2 focus:ring-indigo-500 min-h-[300px] text-gray-800 leading-relaxed shadow-inner"
                                        placeholder="Esempio: Il cliente si identifica con il 'vecchio treno'. Dice: 'Sono stanco e arrugginito, ma continuo a correre...'. Emerge il bisogno di riposo..."
                                        value={analysisData.fullAnalysis || ''}
                                        onChange={(e) => setAnalysisData({ ...analysisData, fullAnalysis: e.target.value })}
                                    />

                                    <div className="mt-6 flex justify-end">
                                        <button
                                            onClick={saveAnalysis}
                                            className="px-8 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-brand-600 text-white font-bold hover:shadow-lg transform transition hover:scale-105 flex items-center gap-2"
                                        >
                                            <span>💾</span> Salva Analisi
                                        </button>
                                    </div>
                                </div>

                                {/* Gestalt Prompts (Static Reference) */}
                                <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-xl">
                                    <h5 className="font-bold text-amber-900 mb-4 flex items-center gap-2">
                                        <span>💡</span> Spunti per l'Esplorazione Gestaltica
                                    </h5>
                                    <div className="space-y-4 text-sm text-amber-900">
                                        <div className="bg-white/60 p-3 rounded-lg">
                                            <strong className="block text-amber-800 mb-1">1. Identificazione (Io sono...)</strong>
                                            Invita il cliente a "diventare" un elemento del sogno (anche inanimato) e a descriversi in prima persona presente.
                                            <br /><em className="text-amber-700/80">"Io sono il mare in tempesta e mi sento..."</em>
                                        </div>
                                        <div className="bg-white/60 p-3 rounded-lg">
                                            <strong className="block text-amber-800 mb-1">2. Dialogo tra le Parti</strong>
                                            Se ci sono elementi in contrasto, falli parlare tra loro direttamente.
                                            <br /><em className="text-amber-700/80">"Tu, scoglio, mi stai bloccando! E tu, onda, sei troppo irruenta..."</em>
                                        </div>
                                        <div className="bg-white/60 p-3 rounded-lg">
                                            <strong className="block text-amber-800 mb-1">3. Messaggio Esistenziale</strong>
                                            Cosa dice questo sogno sulla tua vita attuale? Quale bisogno insoddisfatto o risorsa nascosta sta emergendo?
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header ... */}
            <div className="bg-white rounded-3xl shadow-lg p-6">
                <div className="flex items-center gap-4 mb-4">
                    <span className="text-5xl">🌙</span>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Analisi Gestaltica dei Sogni</h2>
                        <p className="text-gray-600">
                            In Gestalt il sogno è una proiezione esistenziale: ogni elemento del sogno è una parte del sognatore.
                            Seleziona un sogno condiviso dal diario per iniziare il lavoro di integrazione.
                        </p>
                    </div>
                </div>
                <div className="pt-4 border-t border-gray-100 flex justify-end">
                    <button
                        onClick={() => setIsCreating(!isCreating)}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-indigo-700 transition flex items-center gap-2"
                    >
                        <span>{isCreating ? '❌ Annulla' : '+ Inserisci Sogno Manualmente'}</span>
                    </button>
                </div>
            </div>

            {isCreating && (
                <div className="bg-indigo-50 rounded-3xl p-6 border-2 border-indigo-100 animate-fade-in mb-6">
                    <h3 className="font-bold text-indigo-900 mb-4">✍️ Trascrizione Nuovo Sogno</h3>
                    <div className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <input
                                type="text"
                                className="w-full p-3 border rounded-xl"
                                placeholder="Titolo del sogno"
                                value={newDreamForm.title}
                                onChange={e => setNewDreamForm({ ...newDreamForm, title: e.target.value })}
                            />
                            <input
                                type="date"
                                className="w-full p-3 border rounded-xl"
                                value={newDreamForm.date}
                                onChange={e => setNewDreamForm({ ...newDreamForm, date: e.target.value })}
                            />
                        </div>
                        <textarea
                            className="w-full p-3 border rounded-xl min-h-[120px]"
                            placeholder="Racconto del sogno..."
                            value={newDreamForm.text}
                            onChange={e => setNewDreamForm({ ...newDreamForm, text: e.target.value })}
                        ></textarea>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={handleCreateDream}
                                className="bg-indigo-600 text-white px-6 py-2 rounded-xl font-bold"
                            >
                                Salva e Analizza
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {dreams.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                    <p className="text-gray-400">Nessun sogno trovato nel diario condiviso.</p>
                    <p className="text-xs text-gray-400 mt-2">Chiedi al cliente di taggare le note con la categoria "Sogni", oppure inseriscilo manualmente.</p>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {dreams.map(dream => {
                        const isAnalyzed = analyzedDreamIds.has(dream.id);
                        return (
                            <div
                                key={dream.id}
                                onClick={() => handleSelectDream(dream)}
                                className={`bg-white p-6 rounded-2xl shadow-md hover:shadow-xl hover:scale-105 transition cursor-pointer border-t-4 group relative ${isAnalyzed ? 'border-green-500' : 'border-indigo-400'}`}
                            >
                                {isAnalyzed && (
                                    <div className="absolute top-2 right-2 bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">
                                        ✅ Analizzato
                                    </div>
                                )}
                                <div className="text-xs font-bold text-gray-400 mb-2 flex justify-between">
                                    <span>{new Date(dream.timestamp).toLocaleDateString()}</span>
                                </div>
                                <h3 className="font-bold text-gray-800 mb-2 group-hover:text-indigo-600">
                                    {dream.content.title || "Sogno senza titolo"}
                                </h3>
                                <p className="text-sm text-gray-600 line-clamp-3">
                                    {dream.content.text || dream.content}
                                </p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default DreamAnalysisTool;
